import {
  Badge,
  Button,
  DatePicker,
  Dropdown,
  Form,
  Select,
  Space,
  Tag,
  Tooltip,
} from 'antd'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import qs from 'qs'
import React, { useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { Tuning2OutlineIcon } from '../../../components/AppIcon'
import SelectGroupPopup from '../../../components/SelectGroupPopup/SelectGroupPopup'
import SelectPeoplePopup from '../../../components/SelectPeoplePopup/SelectPeoplePopup'
import {
  ASSIGNEE_TYPE,
  DATE_FORMAT_DEFAULT,
  ISO_DATE_FORMAT,
  WORK_STATUS,
} from '../../../constants'
import { checkValidFormFieldValue } from '../../../utils/helper'
import { blue, yellowPrimary } from '../../../color'
import { BankOutlined, TeamOutlined, UserOutlined } from '@ant-design/icons'
import { SelectList } from '../../MissionCreatePage/MissionCreatePageStyled'
import { AvatarHasName } from '../../../components/Common/Avatar'

const { Option } = Select
const { RangePicker } = DatePicker

/**
 * Build chuỗi assignee URL cho 1 type (CREATE / HANDLER).
 * Format: "assignee=encodedId,assigneeType,permissionType,fullName"
 */
const buildAssigneeString = (
  selectGroupData,
  selectUserData,
  selectDepartmentData,
  type,
  urlPermissionType
) => {
  const source =
    selectGroupData[type]?.[0] ||
    selectUserData[type]?.[0] ||
    selectDepartmentData[type]?.[0]

  if (!source) return null

  const assigneeType = selectGroupData[type]?.[0]
    ? ASSIGNEE_TYPE.GROUP
    : selectUserData[type]?.[0]
    ? ASSIGNEE_TYPE.USER
    : ASSIGNEE_TYPE.DEPARTMENT

  return `assignee=${encodeURIComponent(
    source.id
  )},${assigneeType},${urlPermissionType},${source.full_name || 'unknown'}`
}

const ListWorkAdvanceFilterV2 = props => {
  const { workStore, selectPeopleStore, handleChangeParams } = props

  const [showFilter, setShowFilter] = useState(false)
  const [form] = Form.useForm()

  const location = useLocation()
  const history = useHistory()
  const queryStringParse = useMemo(
    () => qs.parse(location.search.replace('?', '')),
    [location.search]
  )

  const {
    selectUserData,
    selectGroupData,
    selectDepartmentData,
    isVisibleSelectGroupPopup,
    handleOpenSelectGroupPopup,
    isVisibleSelectUserDepartmentPopup,
    handleCancelSelectUserDepartment,
    handleCancelSelectGroup,
    setIsVisibleSelectGroupPopup,
    setIsVisibleSelectUserDepartmentPopup,
  } = selectPeopleStore

  const { queryParams } = workStore

  const [createList, setCreateList] = useState([])
  const [handlerList, setHandlerList] = useState([])

  useEffect(() => {
    setCreateList([
      ...(selectUserData[ASSIGNEE_TYPE.CREATE] || []),
      ...(selectDepartmentData[ASSIGNEE_TYPE.CREATE] || []),
      ...(selectGroupData[ASSIGNEE_TYPE.CREATE] || []),
    ])

    setHandlerList([
      ...(selectUserData[ASSIGNEE_TYPE.COMBINER] || []),
      ...(selectDepartmentData[ASSIGNEE_TYPE.COMBINER] || []),
      ...(selectGroupData[ASSIGNEE_TYPE.COMBINER] || []),
    ])
  }, [selectUserData, selectDepartmentData, selectGroupData])

  useEffect(() => {
    if (showFilter) {
      form.setFieldsValue({
        permission: queryStringParse.permission || undefined,
        date:
          queryStringParse.from_date && queryStringParse.to_date
            ? [
                moment(queryStringParse.from_date),
                moment(queryStringParse.to_date),
              ]
            : undefined,
        status: queryStringParse.status || undefined,
      })
    }
  }, [showFilter, queryStringParse, form])

  const onToggleFilter = () => {
    setShowFilter(prev => !prev)
  }

  // Chỉ đóng popup — việc apply assignee sẽ xảy ra khi user bấm "Áp dụng"
  const handleSubmitSelect = () => {
    setIsVisibleSelectGroupPopup(false)
    setIsVisibleSelectUserDepartmentPopup(false)
  }

  const buildAssigneeParams = () => {
    const assigneeCreate = buildAssigneeString(
      selectGroupData,
      selectUserData,
      selectDepartmentData,
      ASSIGNEE_TYPE.CREATE,
      ASSIGNEE_TYPE.CREATE
    )
    // COMBINER trong store nhưng URL permission là HANDLER (giữ đúng format cũ)
    const assigneeHandler = buildAssigneeString(
      selectGroupData,
      selectUserData,
      selectDepartmentData,
      ASSIGNEE_TYPE.COMBINER,
      ASSIGNEE_TYPE.HANDLER
    )

    const assigneeList = [assigneeCreate, assigneeHandler].filter(Boolean)

    // Format assignee dùng cho API (bỏ fullName ở cuối)
    const assigneeForQuery = assigneeList
      .map(item => {
        const parts = item.split(',')
        parts.pop()
        return parts.join(',')
      })
      .join('&')

    return {
      assigneeList, // dùng cho URL (arrayFormat index)
      assigneeForQuery, // dùng cho workStore.queryParams
      // Các individual key để hiển thị lại khi đọc URL
      [ASSIGNEE_TYPE.CREATE]: assigneeCreate
        ? decodeURIComponent(assigneeCreate.split(',')[0].substring(9))
        : null,
      [ASSIGNEE_TYPE.COMBINER]: assigneeHandler
        ? decodeURIComponent(assigneeHandler.split(',')[0].substring(9))
        : null,
    }
  }

  const onFinish = values => {
    const assigneeParams = buildAssigneeParams()

    const params = {
      permission: values.permission || null,
      from_date: values.date
        ? moment(values.date[0]).format(ISO_DATE_FORMAT)
        : null,
      to_date: values.date
        ? moment(values.date[1]).format(ISO_DATE_FORMAT)
        : null,
      status: values.status || null,
      parent_id: null,
      page: 0,
    }

    if (typeof handleChangeParams === 'function') {
      handleChangeParams(params)
    } else {
      workStore.setQueryParams({
        ...queryParams,
        ...params,
        assignee: assigneeParams.assigneeForQuery,
      })

      const queryStringStringify = qs.stringify(
        {
          ...queryStringParse,
          ...params,
          assignee: assigneeParams.assigneeList,
          [ASSIGNEE_TYPE.CREATE]: assigneeParams[ASSIGNEE_TYPE.CREATE],
          [ASSIGNEE_TYPE.COMBINER]: assigneeParams[ASSIGNEE_TYPE.COMBINER],
        },
        {
          skipNulls: true,
        }
      )
      history.replace(`/works?${queryStringStringify}`)
    }

    onToggleFilter()
  }

  const values = Form.useWatch([], form)
  const hasFormValue =
    Object.values(values || {}).some(checkValidFormFieldValue) ||
    createList.length > 0 ||
    handlerList.length > 0

  const appliedFilterCount = useMemo(() => {
    let count = 0
    const hasValue = val => val && String(val).trim() !== ''

    if (hasValue(queryStringParse.permission)) count++
    if (queryStringParse.from_date || queryStringParse.to_date) count++
    if (hasValue(queryStringParse.status)) count++
    if (createList.length > 0) count++
    if (handlerList.length > 0) count++

    return count
  }, [queryStringParse, createList, handlerList])

  return (
    <Dropdown
      overlayStyle={{
        zIndex: 200,
      }}
      open={showFilter}
      onOpenChange={onToggleFilter}
      trigger={['click']}
      dropdownRender={() => (
        <div
          className="custom-page-filter-dropdown-container"
          style={{ width: 380 }}>
          <Form form={form} onFinish={onFinish} layout="vertical">
            <div className="form-elements">
              {/* Quyền xem */}
              <Form.Item
                label="Hiển thị theo"
                name="permission"
                style={{ marginBottom: 16 }}>
                <Select
                  allowClear
                  placeholder={'Hiển thị theo'}
                  style={{ width: '100%' }}
                  className="h-40">
                  <Option value={ASSIGNEE_TYPE.CREATE}>Đã tạo</Option>
                  <Option value={ASSIGNEE_TYPE.HANDLER}>Tôi xử lý</Option>
                  <Option value={ASSIGNEE_TYPE.FOLLOWER}>Tôi theo dõi</Option>
                  <Option value={ASSIGNEE_TYPE.OTHER}>Cần chú ý</Option>
                </Select>
              </Form.Item>

              {/* Khoảng thời gian */}
              <Form.Item
                label="Khoảng thời gian"
                name="date"
                style={{ marginBottom: 16 }}>
                <RangePicker
                  placeholder={['Bắt đầu', 'Kết thúc']}
                  style={{ width: '100%' }}
                  className="h-40"
                  format={[DATE_FORMAT_DEFAULT, DATE_FORMAT_DEFAULT]}
                />
              </Form.Item>

              {/* Trạng thái */}
              <Form.Item
                label="Trạng thái"
                name="status"
                style={{ marginBottom: 16 }}>
                <Select
                  allowClear
                  placeholder={'Trạng thái'}
                  style={{ width: '100%' }}
                  className="h-40">
                  <Option value={WORK_STATUS.COMPLETE}>Hoàn thành</Option>
                  <Option value={WORK_STATUS.PENDING}>Chưa hoàn thành</Option>
                </Select>
              </Form.Item>

              {/* Người tạo */}
              <div style={{ marginBottom: 16 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                  <span>Người tạo:</span>
                  <Space>
                    <Tooltip title="Chọn người dùng" color={blue}>
                      <Button
                        className="select-user-icon-btn"
                        icon={<UserOutlined style={{ color: blue }} />}
                        onClick={() =>
                          selectPeopleStore.handleOpenSelectUserDepartmentPopup(
                            ASSIGNEE_TYPE.CREATE
                          )
                        }
                      />
                    </Tooltip>
                  </Space>
                </div>

                <SelectList>
                  {createList.map(el => (
                    <Tag
                      className={'tag-selected'}
                      color={'blue'}
                      key={el.id}
                      closable
                      onClose={() => {
                        selectPeopleStore.handleRemoveSelect(el)
                      }}>
                      <AvatarHasName
                        imgId={el.image_id}
                        size={22}
                        name={el.full_name}
                        icon={
                          (el.assignee_type === ASSIGNEE_TYPE.DEPARTMENT && (
                            <BankOutlined />
                          )) ||
                          (el.assignee_type === ASSIGNEE_TYPE.GROUP && (
                            <TeamOutlined />
                          ))
                        }
                      />
                    </Tag>
                  ))}
                </SelectList>
              </div>

              {/* Người xử lý */}
              <div style={{ marginBottom: 16 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                  <span>Người xử lý:</span>
                  <Space>
                    <Tooltip title="Chọn người dùng" color={blue}>
                      <Button
                        className="select-user-icon-btn"
                        icon={<UserOutlined style={{ color: blue }} />}
                        onClick={() =>
                          selectPeopleStore.handleOpenSelectUserDepartmentPopup(
                            ASSIGNEE_TYPE.COMBINER
                          )
                        }
                      />
                    </Tooltip>
                    <Tooltip title="Chọn nhóm" color={'#ffc069'}>
                      <Button
                        className="select-user-icon-btn"
                        icon={<TeamOutlined />}
                        style={{ color: yellowPrimary }}
                        onClick={() =>
                          handleOpenSelectGroupPopup(ASSIGNEE_TYPE.COMBINER)
                        }
                      />
                    </Tooltip>
                  </Space>
                </div>
                <SelectList>
                  {handlerList.map(el => (
                    <Tag
                      className={'tag-selected'}
                      color={'blue'}
                      key={el.id}
                      closable
                      onClose={() => {
                        selectPeopleStore.handleRemoveSelect(el)
                      }}>
                      <AvatarHasName
                        imgId={el.image_id}
                        size={22}
                        name={el.full_name}
                        icon={
                          (el.assignee_type === ASSIGNEE_TYPE.DEPARTMENT && (
                            <BankOutlined />
                          )) ||
                          (el.assignee_type === ASSIGNEE_TYPE.GROUP && (
                            <TeamOutlined />
                          ))
                        }
                      />
                    </Tag>
                  ))}
                </SelectList>
              </div>
            </div>

            <div
              style={{
                marginTop: '5px',
                display: 'flex',
                gap: '10px',
                alignItems: 'center',
                justifyContent: hasFormValue ? 'space-between' : 'center',
                padding: '0 20px',
              }}>
              <Button
                className="custom-ant-button"
                type="primary"
                htmlType="submit">
                Áp dụng
              </Button>
              <Button
                className="custom-ant-button"
                htmlType="button"
                onClick={onToggleFilter}>
                Đóng
              </Button>

              <Button
                className="custom-ant-button"
                style={{
                  display: hasFormValue ? 'flex' : 'none',

                  marginLeft: 'auto',
                }}
                htmlType="reset"
                onClick={() => {
                  form.resetFields()

                  // Xóa assignee selections khỏi selectPeopleStore
                  ;[...createList, ...handlerList].forEach(el =>
                    selectPeopleStore.handleRemoveSelect(el)
                  )

                  const params = {
                    permission: null,
                    from_date: null,
                    to_date: null,
                    status: null,
                    assignee: null,
                    [ASSIGNEE_TYPE.CREATE]: null,
                    [ASSIGNEE_TYPE.COMBINER]: null,
                    parent_id: null,
                    page: 0,
                  }

                  if (handleChangeParams) {
                    handleChangeParams(params, true)
                  } else {
                    workStore.setQueryParams({
                      ...queryParams,
                      ...params,
                    })
                    const queryStringStringify = qs.stringify(
                      { ...queryStringParse, ...params },
                      { skipNulls: true }
                    )
                    history.replace(`/works?${queryStringStringify}`)
                  }

                  onToggleFilter()
                }}>
                Xóa
              </Button>
            </div>
          </Form>

          <SelectPeoplePopup
            id={'list-work-advance-filter-select-people-popup'}
            onlySelectOne
            handleCancelSelectUser={handleCancelSelectUserDepartment}
            handleSubmitSelectUser={handleSubmitSelect}
            isVisibleSelectPeoplePopup={isVisibleSelectUserDepartmentPopup}
          />

          <SelectGroupPopup
            id={'list-work-advance-filter-select-group-popup'}
            onlySelectOne
            groupType={'cong_viec'}
            isVisibleSelectGroupPopup={isVisibleSelectGroupPopup}
            handleCancelSelectGroup={handleCancelSelectGroup}
            handleSubmitSelectGroup={handleSubmitSelect}
          />
        </div>
      )}
      placement="bottomRight">
      <Badge count={appliedFilterCount} offset={[-6, -3]}>
        <Button
          className="custom-page-filter-dropdown-btn"
          icon={
            <Tuning2OutlineIcon
              style={{
                width: 16,
                height: 16,
              }}
            />
          }
        />
      </Badge>
    </Dropdown>
  )
}

ListWorkAdvanceFilterV2.propTypes = {}

export default inject(
  'workStore',
  'loadingAnimationStore',
  'selectPeopleStore',
  'authenticationStore'
)(observer(ListWorkAdvanceFilterV2))
